import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PortalDsSelect } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  PORTAL_USER_ACCESS_RINGS,
  type PortalUserAccessRing,
} from '../types/users.types';
import { formatAccessRingLabel } from '../utils/portal-users.mappers';

interface AccessRingEditorProps {
  accessRing: PortalUserAccessRing | null | undefined;
  disabled?: boolean;
  onChange: (accessRing: PortalUserAccessRing) => Promise<{ ok: boolean; message?: string }>;
}

function resolveAccessRing(
  accessRing: PortalUserAccessRing | null | undefined
): PortalUserAccessRing {
  if (accessRing && PORTAL_USER_ACCESS_RINGS.includes(accessRing)) {
    return accessRing;
  }

  return 'all';
}

export function AccessRingEditor({
  accessRing,
  disabled = false,
  onChange,
}: AccessRingEditorProps) {
  const [draftRing, setDraftRing] = useState<PortalUserAccessRing>(() => resolveAccessRing(accessRing));
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraftRing(resolveAccessRing(accessRing));
  }, [accessRing]);

  const handleChange = async(nextRing: PortalUserAccessRing) => {
    const previousRing = resolveAccessRing(accessRing);
    if (nextRing === previousRing) {
      return;
    }

    setDraftRing(nextRing);
    setInlineError(null);
    setIsSaving(true);

    const result = await onChange(nextRing);
    setIsSaving(false);

    if (!result.ok) {
      setDraftRing(previousRing);
      setInlineError(result.message ?? 'Failed to update Early Access.');
    }
  };

  return (
    <div className="flex min-w-[180px] flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <PortalDsSelect
          value={draftRing}
          disabled={disabled || isSaving}
          aria-label="Early Access"
          onChange={(event) => {
            void handleChange(event.target.value as PortalUserAccessRing);
          }}
          className="h-8 min-w-[140px] text-[12px]"
        >
          {PORTAL_USER_ACCESS_RINGS.map((option) => (
            <option
              key={option}
              value={option}
            >
              {formatAccessRingLabel(option)}
            </option>
          ))}
        </PortalDsSelect>
        {isSaving ? (
          <Loader2
            className="size-3.5 animate-spin text-[#9CA3AF]"
            aria-hidden
          />
        ) : null}
      </div>
      {inlineError ? (
        <p className={cn('text-[11px] text-[#DC2626]')}>
          {inlineError}
        </p>
      ) : (
        <span className="text-[11px] text-[#9CA3AF]">
          Controls early access to in-development features — not the user&apos;s plan or billing.
          Alpha includes everything in Beta, plus features still in alpha.
        </span>
      )}
    </div>
  );
}
